import Variable, {ValueType} from './metadata';
import {filterValue, TestType} from 'quantum-graph';
import {dataRows} from './data-types';
import {floorToPercentPrecision} from './number-utils';

export default interface Filter {
    variable: Variable,
    test: TestType,
    value: filterValue | Array<filterValue>,
    // eslint-disable-next-line semi
}

export function applyFilter(data: dataRows, filter: Filter): dataRows {
    return data.filter(row => {
        let value = filter.variable.getValue(row);
        let filterValue = filter.value;
        if (filter.variable.getValueType() === ValueType.Number) {
            value = Number(value);
            filterValue = filterValue instanceof Array ? filterValue.map(v => Number(v)) : Number(filterValue);
        }

        // Special handling for percentfloor - match by display value (floored) for all numeric comparisons
        const format = filter.variable.getTag('Format')?.toLowerCase();
        if (format === 'percentfloor' && (
            filter.test === TestType.Eq ||
            filter.test === TestType.NEq ||
            filter.test === TestType.Gt ||
            filter.test === TestType.GtEq ||
            filter.test === TestType.Lt ||
            filter.test === TestType.LtEq
        )) {
            const precisionTag = filter.variable.getTag('Precision');
            const precision = precisionTag ? Number(precisionTag) : 0.1;

            if (value !== null && value !== undefined && !isNaN(Number(value))) {
                // Floor both the stored value and filter value to match display formatting
                const flooredStored = floorToPercentPrecision(Number(value), precision);
                const flooredFilter = floorToPercentPrecision(Number(filterValue), precision);

                if (filter.test === TestType.Eq) {
                    return flooredStored === flooredFilter;
                } else if (filter.test === TestType.NEq) {
                    return flooredStored !== flooredFilter;
                } else if (filter.test === TestType.Gt) {
                    return flooredStored > flooredFilter;
                } else if (filter.test === TestType.GtEq) {
                    return flooredStored >= flooredFilter;
                } else if (filter.test === TestType.Lt) {
                    return flooredStored < flooredFilter;
                } else if (filter.test === TestType.LtEq) {
                    return flooredStored <= flooredFilter;
                }
            }
            // If value is null/undefined, fall through to normal comparison check
        }

        if (filter.test === TestType.Lt)
            return (value === null || value === undefined) ? false : value < filterValue;
        else if (filter.test === TestType.LtEq)
            return (value === null || value === undefined) ? false : value <= filterValue;
        else if (filter.test === TestType.Eq)
            // eslint-disable-next-line eqeqeq
            return value == filterValue;
        else if (filter.test === TestType.NEq)
            // eslint-disable-next-line eqeqeq
            return value != filterValue;
        else if (filter.test === TestType.GtEq)
            return (value === null || value === undefined) ? false : value >= filterValue;
        else if (filter.test === TestType.Gt)
            return (value === null || value === undefined) ? false : value > filterValue;
        else if (filter.test === TestType.In)
            return (value === null || value === undefined) ? false : (filterValue as Array<filterValue>).includes(value);
        else if (filter.test === TestType.NotIn)
            return (value === null || value === undefined) ? false : !(filterValue as Array<filterValue>).includes(value);
        else if (filter.test === TestType.Like) {
            let search = String(filterValue).toLowerCase();
            search = search.substring(1, search.length - 1);
            return (value === null || value === undefined) ? false : String(value).toLowerCase().includes(search);
        } else if (filter.test === TestType.Null)
            return value === null || value === undefined;
        else if (filter.test === TestType.NotNull)
            return value !== null && value !== undefined;
        return false;
    });
}